/**
 * Backend-issued context token storage.
 *
 * The context token is a backend-issued JWT carrying session_id, main_user_id,
 * active_user_id, sponsor_id, and role flags. It is sent as the X-Context-Token
 * header on authenticated requests and as a query param on the WebSocket handshake.
 *
 * Storage differs by platform:
 *  - Web:    sessionStorage (tab-scoped — each tab owns its own context)
 *  - Native: MMKV (single app-scoped slot — no tab concept)
 *
 * The util exposes both an imperative API (for non-React callers like api.ts)
 * and a React hook (`useContextToken`) backed by `useSyncExternalStore` so
 * components re-render when the token changes.
 */
import { useSyncExternalStore } from "react"

import { storage } from "src/utils/storage/mmkv"

const CONTEXT_TOKEN_KEY = "contextToken"

const isWeb = typeof sessionStorage !== "undefined"

const listeners = new Set<() => void>()
const notify = () => listeners.forEach((listener) => listener())

export function getContextToken(): string | null {
  if (isWeb) return sessionStorage.getItem(CONTEXT_TOKEN_KEY)
  return storage.getString(CONTEXT_TOKEN_KEY) ?? null
}

export function setContextToken(token: string): void {
  if (isWeb) sessionStorage.setItem(CONTEXT_TOKEN_KEY, token)
  else storage.set(CONTEXT_TOKEN_KEY, token)
  notify()
}

export function clearContextToken(): void {
  if (isWeb) sessionStorage.removeItem(CONTEXT_TOKEN_KEY)
  else storage.delete(CONTEXT_TOKEN_KEY)
  notify()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * React hook that reactively observes the context token. Components that
 * consume this re-render whenever `setContextToken` or `clearContextToken`
 * is called — driving WebSocket reconnects on role/sponsor switch.
 *
 * `sessionStorage` does not fire `storage` events for changes in the same
 * tab, so we use a custom subscriber model rather than depending on the
 * native browser event.
 */
export function useContextToken(): string | null {
  return useSyncExternalStore(subscribe, getContextToken, () => null)
}

// ── Intentional context replacement window ───────────────────────────────────
//
// When the client deliberately rotates the context token (POST /context/switch,
// POST /context/refresh, POST /context/reactivate) the backend
// invalidates the previous session_id. Any coincident traffic on the old
// session — a parallel HTTP request that was already on the wire, or a
// WebSocket holding the old session_id — will then fail with
// SESSION_DEACTIVATED / 4001 even though nothing is actually wrong from the
// user's perspective.
//
// `markIntentionalContextReplacement(durationMs)` opens a short grace window
// (default 5s) that error handlers can consult to suppress the takeover
// dialog and skip redundant silent-recovery work. The HTTP layer (api.ts
// axios interceptor) just retries the failing request once the rotation has
// stored the fresh token; the WS layer (WebsocketContext.wsOnClose) lets
// the useContextToken-driven reconnect take over.

const INTENTIONAL_CONTEXT_REPLACEMENT_WINDOW_MS = 5000

let intentionalContextReplacementUntil = 0

/**
 * Open the suppression window. Call this immediately before any operation
 * that intentionally invalidates the current context token (sponsor/role
 * switch (POST /context/switch, /context/refresh, /context/reactivate).
 */
export function markIntentionalContextReplacement(
  durationMs: number = INTENTIONAL_CONTEXT_REPLACEMENT_WINDOW_MS,
): void {
  // `Math.max` so consecutive overlapping rotations extend the window rather
  // than truncate it (e.g. switch sponsor → switch role in quick succession).
  intentionalContextReplacementUntil = Math.max(
    intentionalContextReplacementUntil,
    Date.now() + durationMs,
  )
}

export function isIntentionalContextReplacementInProgress(): boolean {
  return Date.now() < intentionalContextReplacementUntil
}

/**
 * Test/utility helper: force the window closed. Production code should not
 * normally call this — let the timestamp expire naturally.
 */
export function clearIntentionalContextReplacementWindow(): void {
  intentionalContextReplacementUntil = 0
}

// ── Cross-tab context-token ownership detection (web-only) ──────────────────
//
// sessionStorage is supposed to be tab-scoped — each tab "owns" its own
// context_token. There's a real browser quirk on most platforms (notably
// Chrome/Edge on macOS): when a link with `target="_blank"` (or Cmd+click) is
// followed, the new tab inherits the opener tab's sessionStorage. Both tabs
// then think they own the same context_token, and the moment one rotates
// (sponsor switch, role switch) the other gets WS 4001 / 401
// SESSION_DEACTIVATED out of nowhere.
//
// Mitigation: each tab generates a per-runtime owner ID at module init,
// listens on a BroadcastChannel for ownership questions, and answers "I own
// that token" only when its sessionStorage matches the queried token. A
// newly-mounted tab asks "anyone else own this token?" before trusting the
// inherited sessionStorage. If anyone responds, the new tab clears its local
// copy and falls through to POST /context/switch (isLogin:true) to mint its own session.
//
// Native (MMKV) builds skip this entirely — `BroadcastChannel` is undefined
// and there is no opener-inheritance concept on native.

const CONTEXT_TOKEN_OWNERSHIP_CHANNEL = "context-token-ownership"

type ContextTokenOwnershipRequest = {
  type: "context-token-ownership-request"
  requesterId: string
  contextToken: string
}

type ContextTokenOwnershipResponse = {
  type: "context-token-ownership-response"
  requesterId: string
  ownerId: string
}

// Per-runtime UUID. Two tabs always get different IDs (each runs its own JS
// runtime). We use this to (a) identify the requester so we don't answer our
// own questions and (b) tag responses so the requester can verify the
// answer didn't come from itself (defense-in-depth).
const runtimeContextOwnerId =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`

let ownershipChannel: BroadcastChannel | null = null
let ownershipResponderInitialized = false

function getOwnershipChannel(): BroadcastChannel | null {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
    return null
  }
  if (!ownershipChannel) {
    ownershipChannel = new BroadcastChannel(CONTEXT_TOKEN_OWNERSHIP_CHANNEL)
  }
  return ownershipChannel
}

/**
 * Idempotent. Subscribes to the cross-tab ownership channel so this tab will
 * respond to other tabs' "anyone else own this token?" queries. Safe to call
 * multiple times — subsequent calls are no-ops.
 *
 * No-op on platforms without `BroadcastChannel` (i.e. native / older
 * browsers / SSR).
 */
export function initializeContextTokenOwnershipResponder(): void {
  const channel = getOwnershipChannel()
  if (!channel || ownershipResponderInitialized) return

  ownershipResponderInitialized = true

  channel.addEventListener("message", (event: MessageEvent<unknown>) => {
    const data = event.data as ContextTokenOwnershipRequest | undefined
    if (!data || data.type !== "context-token-ownership-request") return
    // Don't answer our own questions (covers the rare same-runtime echo case).
    if (data.requesterId === runtimeContextOwnerId) return

    const stored = getContextToken()
    if (!stored || stored !== data.contextToken) return

    channel.postMessage({
      type: "context-token-ownership-response",
      requesterId: data.requesterId,
      ownerId: runtimeContextOwnerId,
    } satisfies ContextTokenOwnershipResponse)
  })
}

/**
 * Broadcasts an ownership question for `contextToken` and waits up to
 * `timeoutMs` (default 150ms) for any tab to claim it. Returns `true` if any
 * other tab responded "I own this token" — meaning the local copy is a
 * sessionStorage inheritance artifact and should be discarded. Returns
 * `false` on timeout, on platforms without `BroadcastChannel`, or when
 * `contextToken` is empty.
 *
 * Auto-initializes the responder so this tab can answer others' future
 * queries even on the boot path.
 */
export async function isContextTokenOwnedByAnotherTab(
  contextToken: string,
  timeoutMs: number = 150,
): Promise<boolean> {
  const channel = getOwnershipChannel()
  if (!channel || !contextToken) return false

  initializeContextTokenOwnershipResponder()

  return await new Promise<boolean>((resolve) => {
    let settled = false

    const handleMessage = (event: MessageEvent<unknown>) => {
      const data = event.data as ContextTokenOwnershipResponse | undefined
      if (!data || data.type !== "context-token-ownership-response") return
      // Only consider responses addressed to us…
      if (data.requesterId !== runtimeContextOwnerId) return
      // …and only if they came from a different runtime (someone else owns it).
      if (data.ownerId === runtimeContextOwnerId) return
      settled = true
      cleanup()
      resolve(true)
    }

    const cleanup = () => {
      channel.removeEventListener("message", handleMessage)
      clearTimeout(timeoutId)
    }

    const timeoutId = setTimeout(() => {
      if (settled) return
      cleanup()
      resolve(false)
    }, timeoutMs)

    channel.addEventListener("message", handleMessage)
    channel.postMessage({
      type: "context-token-ownership-request",
      requesterId: runtimeContextOwnerId,
      contextToken,
    } satisfies ContextTokenOwnershipRequest)
  })
}

/**
 * Returns this tab's per-runtime owner ID. Exposed for tests; production
 * code should not need this.
 */
export function getRuntimeContextOwnerIdForTesting(): string {
  return runtimeContextOwnerId
}

/**
 * Test helper: tear down the cached BroadcastChannel + responder so a test
 * can re-exercise the lazy-init path. Production code should never call this.
 */
export function resetContextTokenOwnershipForTesting(): void {
  if (ownershipChannel) {
    try {
      ownershipChannel.close()
    } catch {
      // ignore — channel may already be torn down by the runtime
    }
  }
  ownershipChannel = null
  ownershipResponderInitialized = false
}
